import React from 'react';
import styled from 'styled-components';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHome,
  faLocationDot,
  faFileInvoice,
  faUserPlus,
  faSignOutAlt,
  faQuestionCircle,
  faBars
} from '@fortawesome/free-solid-svg-icons';
import { APP_ROUTES } from '../../config/api.config';
import { useLogoutHandler } from '../../utils/logoutHandler';
import LogoutConfirmDialog from '../LogoutConfirmDialog';
import logoMain from '../../assets/images/logo-main.png';

// Define props interface
interface SidebarProps {
  isCollapsed: boolean;
  isMobileOpen: boolean;
  onMobileClose: () => void;
}

const SidebarContainer = styled.div<{ $isCollapsed: boolean; $isMobileOpen: boolean }>`
  width: ${props => props.$isCollapsed ? '70px' : '240px'};
  background: linear-gradient(135deg, #ff6b6b 0%, #ff8e53 100%);
  color: white;
  transition: all 0.3s ease;
  position: fixed;
  height: 100vh;
  left: 0;
  top: 0;
  padding-top: 20px;
  z-index: 1000;

  @media (max-width: 768px) {
    transform: translateX(${props => props.$isMobileOpen ? '0' : '-100%'});
    width: 240px;
    box-shadow: ${props => props.$isMobileOpen ? '2px 0 8px rgba(0, 0, 0, 0.15)' : 'none'};
    z-index: 1002;
  }
`;

const Logo = styled.div<{ $isCollapsed: boolean }>`
  padding: ${props => props.$isCollapsed ? '0 10px' : '0 24px'};
  margin-bottom: 30px;
  text-align: ${props => props.$isCollapsed ? 'center' : 'left'};

  img {
    max-width: ${props => props.$isCollapsed ? '40px' : '160px'};
    height: auto;
  }

  @media (max-width: 768px) {
    padding: 0 24px;
    text-align: left;
    
    img {
      max-width: 160px;
    }
  }
`;

const MenuList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
`;

const MenuItem = styled.li<{ active?: boolean }>`
  margin: 8px 12px;
  border-radius: 8px;
  background: ${props => props.active ? 'rgba(255, 255, 255, 0.1)' : 'transparent'};

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  @media (max-width: 768px) {
    margin: 4px;
  }
`;

const MenuLink = styled(Link) <{ $isCollapsed: boolean }>`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  color: white;
  text-decoration: none;
  font-size: 16px;

  svg {
    width: 20px;
    margin-right: ${props => props.$isCollapsed ? '0' : '12px'};
    font-size: 18px;
  }

  span {
    display: ${props => props.$isCollapsed ? 'none' : 'block'};
  }

  @media (max-width: 768px) {
    svg {
      margin-right: 12px;
    }

    span {
      display: block;
    }
  }
`;

const LogoutButton = styled.button<{ $isCollapsed: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 12px 16px;
  background: transparent;
  border: none;
  color: white;
  font-size: 16px;
  cursor: pointer;
  text-align: left;

  svg {
    width: 20px;
    margin-right: ${props => props.$isCollapsed ? '0' : '12px'};
  }

  span {
    display: ${props => props.$isCollapsed ? 'none' : 'block'};
  }

  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }

  @media (max-width: 768px) {
    svg {
      margin-right: 12px;
    }

    span {
      display: block;
    }
  }
`;

const MainMenuItems = styled.div`
  flex: 1;
`;

const LogoutContainer = styled.div`
  margin-top: auto;
  margin-bottom: 20px;
`;

const Overlay = styled.div<{ $isVisible: boolean }>`
  display: none;
  
  @media (max-width: 768px) {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 1001;
    opacity: ${props => props.$isVisible ? 1 : 0};
    visibility: ${props => props.$isVisible ? 'visible' : 'hidden'};
    transition: all 0.3s ease;
    backdrop-filter: blur(3px);
  }
`;

const Sidebar: React.FC<SidebarProps> = ({ isCollapsed, isMobileOpen, onMobileClose }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const {
    showLogoutConfirm,
    handleLogoutClick,
    handleLogoutConfirm,
    handleLogoutCancel
  } = useLogoutHandler();

  const handleLogout = () => {
    handleLogoutClick();
  };

  return (
    <>
      <Overlay $isVisible={isMobileOpen} onClick={onMobileClose} />

      <SidebarContainer $isCollapsed={isCollapsed} $isMobileOpen={isMobileOpen}>
        <Logo $isCollapsed={isCollapsed}>
          <img src={logoMain} alt="WhatzPro Logo" />
        </Logo>

        <MenuList>
          <MenuItem active={location.pathname === APP_ROUTES.DASHBOARD}>
            <MenuLink to={APP_ROUTES.DASHBOARD} $isCollapsed={isCollapsed}>
              <FontAwesomeIcon icon={faHome} />
              <span>Dashboard</span>
            </MenuLink>
          </MenuItem>
          <MenuItem active={location.pathname === APP_ROUTES.SUBSCRIBED_LOCATIONS}>
            <MenuLink to={APP_ROUTES.SUBSCRIBED_LOCATIONS} $isCollapsed={isCollapsed}>
              <FontAwesomeIcon icon={faLocationDot} />
              <span>Subscribed locations</span>
            </MenuLink>
          </MenuItem>
          <MenuItem active={location.pathname === APP_ROUTES.INVOICE_LIST}>
            <MenuLink to={APP_ROUTES.INVOICE_LIST} $isCollapsed={isCollapsed}>
              <FontAwesomeIcon icon={faFileInvoice} />
              <span>Invoice list</span>
            </MenuLink>
          </MenuItem>
          <MenuItem active={location.pathname === APP_ROUTES.FAQ}>
            <MenuLink to={APP_ROUTES.FAQ} $isCollapsed={isCollapsed}>
              <FontAwesomeIcon icon={faQuestionCircle} />
              <span>FAQ</span>
            </MenuLink>
          </MenuItem>
          <MenuItem>
            <LogoutButton onClick={handleLogout} $isCollapsed={isCollapsed}>
              <FontAwesomeIcon icon={faSignOutAlt} />
              <span>Logout</span>
            </LogoutButton>
          </MenuItem>
        </MenuList>
      </SidebarContainer>

      <LogoutConfirmDialog
        isOpen={showLogoutConfirm}
        onConfirm={handleLogoutConfirm}
        onCancel={handleLogoutCancel}
      />
    </>
  );
};

export default Sidebar; 